import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import JSZip from 'jszip';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import PptxGenJS from 'pptxgenjs';
import { GIFEncoder, applyPalette, quantize } from 'gifenc';
import type { ExportProgress, StoryCell, StoryboardProject } from '../types';
import { getCanvasSize, nowIso, uid } from '../model/factories';
import { projectSchema } from '../model/schema';
import { CellSvg } from '../render/CellSvg';

const safeFileName = (name: string): string =>
  name.trim().replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').slice(0, 120) || 'StoryboardSmith';

const yieldToBrowser = (): Promise<void> => new Promise((resolve) => setTimeout(resolve, 0));

export const downloadBlob = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

export const blobToDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Could not read generated file.'));
    reader.onload = () => resolve(String(reader.result));
    reader.readAsDataURL(blob);
  });

export const dataUrlToBlob = async (dataUrl: string): Promise<Blob> => {
  const response = await fetch(dataUrl);
  return response.blob();
};

export const cellToSvgString = (project: StoryboardProject, cell: StoryCell): string => {
  const markup = renderToStaticMarkup(<CellSvg project={project} cell={cell} includeMetadata />);
  return `<?xml version="1.0" encoding="UTF-8"?>${markup}`;
};

export const svgStringToCanvas = async (
  svg: string,
  width: number,
  height: number,
  scale = 1,
): Promise<HTMLCanvasElement> => {
  const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const image = new Image();
  image.decoding = 'async';
  try {
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('The storyboard could not be rasterised.'));
      image.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const context = canvas.getContext('2d', { alpha: false });
    if (!context) throw new Error('Canvas rendering is not available in this browser.');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas;
  } finally {
    URL.revokeObjectURL(url);
  }
};

export const canvasToBlob = (canvas: HTMLCanvasElement, type = 'image/png', quality?: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new Error('The browser could not create the requested image.'));
    }, type, quality);
  });

export const renderCellPng = async (
  project: StoryboardProject,
  cell: StoryCell,
  scale = 2,
): Promise<Blob> => {
  const { width, height } = getCanvasSize(project.layout.aspectRatio);
  const canvas = await svgStringToCanvas(cellToSvgString(project, cell), width, height, scale);
  return canvasToBlob(canvas);
};

const drawWrappedCanvasText = (
  context: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
): number => {
  const words = text.trim().split(/\s+/).filter(Boolean);
  let line = '';
  let currentY = y;
  words.forEach((word) => {
    const candidate = line ? `${line} ${word}` : word;
    if (context.measureText(candidate).width > maxWidth && line) {
      context.fillText(line, x, currentY);
      line = word;
      currentY += lineHeight;
    } else {
      line = candidate;
    }
  });
  if (line) context.fillText(line, x, currentY);
  return currentY + lineHeight;
};

export const renderBoardPng = async (
  project: StoryboardProject,
  onProgress?: (progress: ExportProgress) => void,
  scale = 1,
): Promise<Blob> => {
  const columns = Math.min(project.layout.columns, project.cells.length);
  const { width: cellWidth, height: cellHeight } = getCanvasSize(project.layout.aspectRatio);
  const previewWidth = 720;
  const previewHeight = (cellHeight / cellWidth) * previewWidth;
  const gutter = 50;
  const margin = 70;
  const titleArea = 150;
  const captionArea = project.layout.showTitles || project.layout.showDescriptions ? 100 : 16;
  const rows = Math.ceil(project.cells.length / columns);
  const boardWidth = margin * 2 + columns * previewWidth + (columns - 1) * gutter;
  const boardHeight = margin * 2 + titleArea + rows * (previewHeight + captionArea) + (rows - 1) * gutter;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(boardWidth * scale);
  canvas.height = Math.round(boardHeight * scale);
  const context = canvas.getContext('2d', { alpha: false });
  if (!context) throw new Error('Canvas rendering is not available.');
  context.scale(scale, scale);
  context.fillStyle = project.layout.background || '#f7f5fc';
  context.fillRect(0, 0, boardWidth, boardHeight);
  context.fillStyle = '#6255f6';
  context.font = '800 24px Inter, Arial, sans-serif';
  context.letterSpacing = '3px';
  context.fillText('COMPLETE STORYBOARD', margin, margin + 25);
  context.letterSpacing = '0px';
  context.fillStyle = '#27243c';
  context.font = '800 54px Inter, Arial, sans-serif';
  context.fillText(project.title, margin, margin + 88);

  for (let index = 0; index < project.cells.length; index += 1) {
    const cell = project.cells[index];
    onProgress?.({ label: `Rendering scene ${index + 1}`, completed: index, total: project.cells.length });
    const png = await renderCellPng(project, cell, 1);
    const imageUrl = URL.createObjectURL(png);
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error(`Scene ${index + 1} could not be rendered.`));
      image.src = imageUrl;
    });
    const row = Math.floor(index / columns);
    const column = index % columns;
    const x = margin + column * (previewWidth + gutter);
    const y = margin + titleArea + row * (previewHeight + captionArea + gutter);

    context.save();
    context.shadowColor = 'rgba(39, 36, 60, .16)';
    context.shadowBlur = 18;
    context.shadowOffsetY = 7;
    context.fillStyle = '#fff';
    context.beginPath();
    context.roundRect(x - 8, y - 8, previewWidth + 16, previewHeight + 16, 18);
    context.fill();
    context.restore();
    context.drawImage(image, x, y, previewWidth, previewHeight);
    URL.revokeObjectURL(imageUrl);

    context.fillStyle = '#3a3650';
    context.font = '800 30px Inter, Arial, sans-serif';
    context.fillText(String(index + 1).padStart(2, '0'), x + 15, y + 38);
    let captionY = y + previewHeight + 44;
    if (project.layout.showTitles) {
      context.fillStyle = '#27243c';
      context.font = '700 27px Inter, Arial, sans-serif';
      context.fillText(cell.title || `Scene ${index + 1}`, x, captionY);
      captionY += 34;
    }
    if (project.layout.showDescriptions && cell.description) {
      context.fillStyle = '#6f6a7d';
      context.font = '400 21px Inter, Arial, sans-serif';
      drawWrappedCanvasText(context, cell.description, x, captionY, previewWidth, 28);
    }
    await yieldToBrowser();
  }
  onProgress?.({ label: 'Finishing board', completed: project.cells.length, total: project.cells.length });
  return canvasToBlob(canvas);
};

export const exportCurrentCellPng = async (project: StoryboardProject, cell: StoryCell): Promise<void> => {
  const blob = await renderCellPng(project, cell, 2);
  downloadBlob(blob, `${safeFileName(project.title)}-${safeFileName(cell.title)}.png`);
};

export const exportBoardPng = async (
  project: StoryboardProject,
  onProgress?: (progress: ExportProgress) => void,
): Promise<void> => {
  const blob = await renderBoardPng(project, onProgress, 1);
  downloadBlob(blob, `${safeFileName(project.title)}-board.png`);
};

export const exportCellsZip = async (
  project: StoryboardProject,
  onProgress?: (progress: ExportProgress) => void,
): Promise<void> => {
  const zip = new JSZip();
  for (let index = 0; index < project.cells.length; index += 1) {
    const cell = project.cells[index];
    onProgress?.({ label: `Rendering scene ${index + 1}`, completed: index, total: project.cells.length });
    const blob = await renderCellPng(project, cell, 2);
    zip.file(`${String(index + 1).padStart(2, '0')}-${safeFileName(cell.title)}.png`, blob);
    await yieldToBrowser();
  }
  const result = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
  downloadBlob(result, `${safeFileName(project.title)}-cells.zip`);
};

export const exportPdf = async (
  project: StoryboardProject,
  onProgress?: (progress: ExportProgress) => void,
): Promise<void> => {
  const pdf = await PDFDocument.create();
  pdf.setTitle(project.title);
  pdf.setAuthor('StoryboardSmith');
  pdf.setCreator('StoryboardSmith browser app');
  const font = await pdf.embedFont(StandardFonts.HelveticaBold);
  const { width, height } = getCanvasSize(project.layout.aspectRatio);
  const pageWidth = 960;
  const pageHeight = (height / width) * pageWidth + 78;

  for (let index = 0; index < project.cells.length; index += 1) {
    const cell = project.cells[index];
    onProgress?.({ label: `Adding PDF page ${index + 1}`, completed: index, total: project.cells.length });
    const blob = await renderCellPng(project, cell, 1.5);
    const bytes = await blob.arrayBuffer();
    const image = await pdf.embedPng(bytes);
    const page = pdf.addPage([pageWidth, pageHeight]);
    page.drawRectangle({ x: 0, y: 0, width: pageWidth, height: pageHeight, color: rgb(0.98, 0.98, 0.995) });
    page.drawImage(image, { x: 0, y: 78, width: pageWidth, height: pageHeight - 78 });
    page.drawText(`${index + 1}. ${cell.title || `Scene ${index + 1}`}`, { x: 28, y: 31, size: 24, font, color: rgb(0.15, 0.14, 0.24) });
    await yieldToBrowser();
  }
  const bytes = await pdf.save();
  downloadBlob(new Blob([bytes], { type: 'application/pdf' }), `${safeFileName(project.title)}.pdf`);
};

export const exportPptx = async (
  project: StoryboardProject,
  onProgress?: (progress: ExportProgress) => void,
): Promise<void> => {
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_WIDE';
  pptx.author = 'StoryboardSmith';
  pptx.subject = project.title;
  pptx.title = project.title;
  pptx.company = 'StoryboardSmith';
  pptx.theme = {
    headFontFace: 'Aptos Display',
    bodyFontFace: 'Aptos',
  };

  for (let index = 0; index < project.cells.length; index += 1) {
    const cell = project.cells[index];
    onProgress?.({ label: `Creating slide ${index + 1}`, completed: index, total: project.cells.length });
    const png = await renderCellPng(project, cell, 1.5);
    const data = await blobToDataUrl(png);
    const slide = pptx.addSlide();
    slide.background = { color: 'F7F5FC' };
    slide.addImage({ data, x: 0, y: 0, w: 13.333, h: 7.5 });
    slide.addNotes(`${cell.title}\n${cell.description}`);
    await yieldToBrowser();
  }
  const output = await pptx.write({ outputType: 'blob', compression: true });
  const blob = output instanceof Blob ? output : new Blob([output as ArrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
  downloadBlob(blob, `${safeFileName(project.title)}.pptx`);
};

export const exportGif = async (
  project: StoryboardProject,
  onProgress?: (progress: ExportProgress) => void,
): Promise<void> => {
  const { width: sourceWidth, height: sourceHeight } = getCanvasSize(project.layout.aspectRatio);
  const width = project.layout.aspectRatio === 'portrait' ? 360 : 640;
  const height = Math.round((sourceHeight / sourceWidth) * width);
  const gif = GIFEncoder();

  for (let index = 0; index < project.cells.length; index += 1) {
    const cell = project.cells[index];
    onProgress?.({ label: `Encoding GIF frame ${index + 1}`, completed: index, total: project.cells.length });
    const source = await svgStringToCanvas(cellToSvgString(project, cell), sourceWidth, sourceHeight, 1);
    const frame = document.createElement('canvas');
    frame.width = width;
    frame.height = height;
    const context = frame.getContext('2d', { alpha: false, willReadFrequently: true });
    if (!context) throw new Error('GIF rendering is not supported.');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, width, height);
    context.drawImage(source, 0, 0, width, height);
    const rgba = context.getImageData(0, 0, width, height).data;
    const palette = quantize(rgba, 256, { format: 'rgb444' });
    const indexed = applyPalette(rgba, palette, 'rgb444');
    gif.writeFrame(indexed, width, height, {
      palette,
      delay: Math.max(100, Math.round(cell.durationMs / 10) * 10),
      repeat: 0,
    });
    await yieldToBrowser();
  }
  gif.finish();
  downloadBlob(new Blob([gif.bytes()], { type: 'image/gif' }), `${safeFileName(project.title)}.gif`);
};

export const exportProjectPackage = async (
  project: StoryboardProject,
  onProgress?: (progress: ExportProgress) => void,
): Promise<void> => {
  onProgress?.({ label: 'Writing project document', completed: 0, total: 3 });
  const zip = new JSZip();
  zip.file('project.json', JSON.stringify(project, null, 2));
  zip.file('manifest.json', JSON.stringify({
    format: 'StoryboardSmith project',
    formatVersion: 1,
    exportedAt: nowIso(),
    projectId: project.id,
    title: project.title,
    cellCount: project.cells.length,
  }, null, 2));
  onProgress?.({ label: 'Creating cover preview', completed: 1, total: 3 });
  const cover = await renderBoardPng(project, undefined, 0.55);
  zip.folder('previews')?.file('cover.png', cover);
  onProgress?.({ label: 'Compressing project', completed: 2, total: 3 });
  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 7 } });
  downloadBlob(blob, `${safeFileName(project.title)}.smithboard`);
};

export const importProjectFile = async (file: File): Promise<StoryboardProject> => {
  let raw: unknown;
  if (file.name.toLowerCase().endsWith('.smithboard') || file.type === 'application/zip') {
    const zip = await JSZip.loadAsync(file);
    const entry = zip.file('project.json');
    if (!entry) throw new Error('This package does not contain project.json.');
    raw = JSON.parse(await entry.async('string'));
  } else {
    raw = JSON.parse(await file.text());
  }
  const parsed = projectSchema.safeParse(raw);
  if (!parsed.success) {
    const message = parsed.error.issues.slice(0, 4).map((issue) => `${issue.path.join('.')}: ${issue.message}`).join('\n');
    throw new Error(`The file is not a valid StoryboardSmith project.\n${message}`);
  }
  const project = parsed.data as StoryboardProject;
  project.id = uid('project');
  project.title = `${project.title} (imported)`;
  project.createdAt = nowIso();
  project.updatedAt = nowIso();
  project.cells = project.cells.map((cell) => ({
    ...cell,
    id: uid('cell'),
    elements: cell.elements.map((element) => ({ ...element, id: uid('el') })),
  }));
  return project;
};
